import classNames from 'classnames'
import { ForwardedRef, forwardRef, useCallback, useMemo } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

import { DatePicker, DatePickerProps } from '@components/date-picker'
import { Button } from '@elements/button'
import { H3 } from '@elements/headings'
import { EventHeader } from '@modules/event/views/event-header'

import {
  getDatePickerDisplayFormatByVenueDateDisplayFormat,
  getLongDateFormatByVenueDateDisplayFormat,
  addToDate,
  getDateFromNow,
  withDateFormat,
} from '@utils/date-time'
import { Venue, VenueEvent } from 'types/model'

export interface InventoryListHeaderProps {
  venue: Venue
  date?: string
  onDateChange?: DatePickerProps['onChange']
  event?: VenueEvent
  showEventPoster?: boolean
  shadow?: boolean
  minDate?: number
  maxDate?: number
}

export const InventoryListHeader = forwardRef(function InventoryListHeader(
  {
    venue,
    event,
    date = new Date().toISOString(),
    onDateChange,
    showEventPoster = true,
    shadow = false,
    minDate = getDateFromNow().valueOf(),
    maxDate,
  }: InventoryListHeaderProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const eventTimezone = event?.timezone || venue.location.timezone
  const dateDisplayFormat = venue.dateDisplayFormat
  const longDateFormat =
    getLongDateFormatByVenueDateDisplayFormat(dateDisplayFormat)

  const prevDate = useCallback(() => {
    const newDate = addToDate(date, { days: -1 })

    if (newDate && onDateChange) {
      onDateChange(newDate.toISOString())
    }
  }, [date, onDateChange])

  const nextDate = useCallback(() => {
    const newDate = addToDate(date, { days: 1 })

    if (newDate && onDateChange) {
      onDateChange(newDate.toISOString())
    }
  }, [date, onDateChange])

  // Date arithmetic to figure out disabled calendar days and disabled pagination
  const todayUTC = useMemo(() => {
    const dateObj = new Date(date)
    return new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      0,
      0,
      0
    ).valueOf()
  }, [date])

  const tomorrowUTC = useMemo(() => {
    const dateObj = new Date(date)
    return new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate() + 1,
      0,
      0,
      0
    ).valueOf()
  }, [date])

  return (
    <div
      className={classNames(
        'w-full space-y-3 md:space-y-6 z-[1] px-3 transition-shadow shadow-none',
        { 'shadow-md': shadow }
      )}
      ref={ref}
    >
      <div className='pt-3 pb-4 border-b border-standard-light'>
        {venue && event ? (
          <EventHeader
            venueName={venue.name}
            description={event.description}
            venueAddress={venue?.location.address}
            eventDate={event.dateStart}
            eventTimezone={eventTimezone}
            eventName={event.name}
            eventPoster={
              showEventPoster ? event.primaryImage?.filename : undefined
            }
          />
        ) : !!(date && onDateChange) ? (
          <div className='flex items-center justify-between w-full'>
            <div className='flex items-center justify-end w-full space-x-2 md:space-x-4'>
              <Button.Tertiary
                sm
                onClick={prevDate}
                disabled={todayUTC <= minDate}
              >
                <ArrowLeftIcon className='w-4 h-4' />
              </Button.Tertiary>
              <DatePicker
                id='inventory-list-date-picker'
                minDate={minDate}
                maxDate={maxDate}
                onChange={onDateChange}
                displayFormat={getDatePickerDisplayFormatByVenueDateDisplayFormat(
                  dateDisplayFormat
                )}
                value={date}
              />
              <Button.Tertiary
                sm
                onClick={nextDate}
                disabled={maxDate ? tomorrowUTC > maxDate : false}
              >
                <ArrowRightIcon className='w-4 h-4' />
              </Button.Tertiary>
            </div>
          </div>
        ) : (
          <H3 className='!text-[24px]'>
            {withDateFormat(date, longDateFormat)}
          </H3>
        )}
      </div>
    </div>
  )
})
